import { useEffect, useRef, useState } from 'react';
import { useToast } from '@busy/ui';
import { useCutTimer } from '../../store/cutTimer';
import { useT } from '../../lib/i18n';
import { PhotoCaptureModal } from '../PhotoCaptureModal';
import { composeBeforeAfter } from '../../lib/photoCompose';

// Top-of-summary section for the before/after photo feature. Walks the
// barber through the four states:
//   1. No before, no after  → twin "Add" CTAs side-by-side
//   2. Before only          → "Snap the after" as the main CTA, before
//                             thumbnail in the corner as a reminder
//   3. After only           → still let them add a before retroactively
//   4. Both present         → composite generates + preview + Share button
//
// The composite is generated client-side via the canvas compositor; once
// generated it's cached in component state until either photo changes.

export function BeforeAfterSummarySection() {
  const t = useT();
  const toast = useToast();
  const beforePhotoUrl = useCutTimer((s) => s.beforePhotoUrl);
  const beforePhotoBlob = useCutTimer((s) => s.beforePhotoBlob);
  const afterPhotoUrl = useCutTimer((s) => s.afterPhotoUrl);
  const afterPhotoBlob = useCutTimer((s) => s.afterPhotoBlob);
  // High-fidelity native sources (when available — populated by the
  // cut timer's before/after capture flow). Lets composeBeforeAfter
  // skip the first-pass JPEG compression and render directly from
  // full-resolution camera blobs.
  const beforePhotoSource = useCutTimer((s) => s.beforePhotoSource);
  const beforePhotoCrop = useCutTimer((s) => s.beforePhotoCrop);
  const afterPhotoSource = useCutTimer((s) => s.afterPhotoSource);
  const afterPhotoCrop = useCutTimer((s) => s.afterPhotoCrop);
  const setBeforePhoto = useCutTimer((s) => s.setBeforePhoto);
  const setAfterPhoto = useCutTimer((s) => s.setAfterPhoto);

  const [capture, setCapture] = useState<'before' | 'after' | null>(null);
  const [composite, setComposite] = useState<{ url: string; blob: Blob } | null>(null);
  const [composing, setComposing] = useState(false);
  const lastInputsRef = useRef<{ before: Blob | null; after: Blob | null }>({
    before: null,
    after: null,
  });

  // Recompose whenever either photo blob changes. Cache by reference so
  // re-renders without input changes don't redo the canvas work.
  useEffect(() => {
    const beforeChanged = lastInputsRef.current.before !== beforePhotoBlob;
    const afterChanged = lastInputsRef.current.after !== afterPhotoBlob;
    if (!beforeChanged && !afterChanged) return;
    lastInputsRef.current = { before: beforePhotoBlob, after: afterPhotoBlob };
    if (composite) {
      URL.revokeObjectURL(composite.url);
      setComposite(null);
    }
    if (!beforePhotoBlob || !afterPhotoBlob) return;
    let cancelled = false;
    setComposing(true);
    // Pass native sources when both halves have them. Either side
    // missing → composeBeforeAfter falls back to the cropped blob for
    // that side (mixed mode still produces a coherent composite).
    const beforeNative =
      beforePhotoSource && beforePhotoCrop
        ? { source: beforePhotoSource, crop: beforePhotoCrop }
        : null;
    const afterNative =
      afterPhotoSource && afterPhotoCrop
        ? { source: afterPhotoSource, crop: afterPhotoCrop }
        : null;
    composeBeforeAfter(
      beforePhotoBlob,
      afterPhotoBlob,
      'portrait',
      beforeNative,
      afterNative,
    )
      .then((blob) => {
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        setComposite({ url, blob });
      })
      .catch(() => {
        if (cancelled) return;
        toast(t('cut.beforeAfter.composeError'), 'error');
      })
      .finally(() => {
        if (!cancelled) setComposing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [beforePhotoBlob, afterPhotoBlob]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup composite URL on unmount.
  useEffect(() => {
    return () => {
      if (composite) URL.revokeObjectURL(composite.url);
    };
  }, [composite]);

  function handleCaptured(blob: Blob) {
    if (capture === 'before') setBeforePhoto(blob);
    else if (capture === 'after') setAfterPhoto(blob);
    setCapture(null);
  }

  async function handleShare() {
    if (!composite) return;
    const file = new File([composite.blob], `before-after-${Date.now()}.jpg`, {
      type: 'image/jpeg',
    });
    const nav = typeof navigator !== 'undefined' ? (navigator as Navigator) : null;
    const title = t('cut.beforeAfter.shareTitle');
    const text = t('cut.beforeAfter.shareBody');
    if (nav?.share && nav.canShare && nav.canShare({ files: [file] })) {
      try {
        await nav.share({ title, text, files: [file] });
        return;
      } catch {
        // user cancelled or share failed — fall through to download
      }
    }
    // Fallback — trigger a download so the barber can share manually.
    const a = document.createElement('a');
    a.href = composite.url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast(t('cut.beforeAfter.downloaded'), 'success');
  }

  const haveBoth = !!beforePhotoUrl && !!afterPhotoUrl;
  const haveBefore = !!beforePhotoUrl;
  const haveAfter = !!afterPhotoUrl;

  return (
    <section className="rounded-2xl border border-line bg-surface overflow-hidden">
      <div className="px-4 pt-3 pb-2 flex items-center gap-2">
        <CameraIcon className="w-4 h-4 text-gold-500" />
        <span className="text-[11px] tracking-[0.16em] uppercase font-extrabold text-ink-700">
          {t('cut.beforeAfter.title')}
        </span>
        {haveBoth && (
          <span className="ml-auto inline-flex items-center gap-1 text-[10px] tracking-[0.14em] uppercase font-extrabold text-gold-500">
            {t('cut.beforeAfter.ready')}
          </span>
        )}
      </div>

      {haveBoth ? (
        <div className="px-4 pb-4 space-y-3">
          {composing ? (
            <div className="aspect-square rounded-xl bg-bg-alt animate-pulse" />
          ) : composite ? (
            <button
              type="button"
              onClick={handleShare}
              className="block w-full rounded-xl overflow-hidden border border-line group focus:outline-none focus:ring-2 focus:ring-gold-500"
            >
              <img
                src={composite.url}
                alt={t('cut.beforeAfter.compositeAlt')}
                className="w-full aspect-square object-cover"
              />
            </button>
          ) : null}
          {/* Share is the only action here — retake lives in the
              capture flow itself before the cut summary lands; once
              the barber arrives at the report, the photos are
              committed and re-shooting them mid-summary feels
              backwards. */}
          <button
            type="button"
            onClick={handleShare}
            disabled={!composite || composing}
            className="w-full h-11 rounded-lg bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 font-bold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50 hover:shadow-md active:scale-[0.99] transition-all"
          >
            <ShareIcon className="w-4 h-4" />
            {t('cut.beforeAfter.share')}
          </button>
        </div>
      ) : (
        <div className="px-4 pb-4 grid grid-cols-2 gap-3">
          <PhotoSlot
            label={t('cut.beforeAfter.before')}
            present={haveBefore}
            url={beforePhotoUrl}
            ctaPresent={t('cut.beforeAfter.retakeBefore')}
            ctaEmpty={t('cut.beforeAfter.addBefore')}
            onClick={() => setCapture('before')}
          />
          <PhotoSlot
            label={t('cut.beforeAfter.after')}
            present={haveAfter}
            url={afterPhotoUrl}
            ctaPresent={t('cut.beforeAfter.retakeAfter')}
            ctaEmpty={t('cut.beforeAfter.addAfter')}
            onClick={() => setCapture('after')}
            highlight={haveBefore && !haveAfter}
          />
        </div>
      )}

      <PhotoCaptureModal
        open={capture !== null}
        title={
          capture === 'before'
            ? t('cut.beforeAfter.captureTitleBefore')
            : t('cut.beforeAfter.captureTitleAfter')
        }
        composeRole={capture ?? undefined}
        onCapture={handleCaptured}
        onClose={() => setCapture(null)}
      />
    </section>
  );
}

function PhotoSlot({
  label,
  present,
  url,
  ctaPresent,
  ctaEmpty,
  onClick,
  highlight = false,
}: {
  label: string;
  present: boolean;
  url: string | null;
  ctaPresent: string;
  ctaEmpty: string;
  onClick: () => void;
  highlight?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative aspect-square rounded-xl overflow-hidden text-left transition-all ${
        present
          ? 'border border-line'
          : highlight
          ? 'border-2 border-gold-500 bg-gold-500/5 animate-pulse-slow'
          : 'border-2 border-dashed border-line bg-bg-alt hover:border-navy-900 dark:hover:border-gold-500'
      }`}
    >
      {present && url ? (
        <>
          <img src={url} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-x-0 bottom-0 px-2 py-1.5 bg-gradient-to-t from-black/85 to-transparent text-white">
            <div className="text-[10px] tracking-[0.16em] uppercase font-extrabold">{label}</div>
            <div className="text-[10px] opacity-80">{ctaPresent}</div>
          </div>
        </>
      ) : (
        <div className="absolute inset-0 grid place-items-center">
          <div className="flex flex-col items-center gap-1.5 text-ink-500">
            <CameraIcon className="w-7 h-7" />
            <div className="text-[10px] tracking-[0.16em] uppercase font-extrabold">{label}</div>
            <div className="text-[10.5px] font-semibold text-ink-700">{ctaEmpty}</div>
          </div>
        </div>
      )}
    </button>
  );
}

function CameraIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  );
}

function ShareIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
      <polyline points="16 6 12 2 8 6" />
      <line x1="12" y1="2" x2="12" y2="15" />
    </svg>
  );
}
